import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';

const FacilityDetail = () => {
  const { id } = useParams();
  const [activeImage, setActiveImage] = useState(0);
  
  // Comprehensive facilities data
  const facilitiesData = {
    // ========== FACILITIES ==========
    'sports-complex': {
      name: 'Sports Complex',
      category: 'Sports',
      type: 'facility',
      description: 'Multi-purpose indoor sports facility for various athletic activities.',
      longDescription: 'Our state-of-the-art Sports Complex features multiple courts and training areas for various sports. With professional-grade flooring, lighting, and equipment, it\'s the perfect venue for both casual play and competitive matches.',
      images: [
        'https://images.unsplash.com/photo-1534438727271-182fc8d7e4e7?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1526676037777-05a232554f77?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1517649763962-0c623066013b?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-basketball-ball',
      capacity: '200 spectators',
      hours: 'Mon-Sun: 6am - 10pm',
      features: [
        'Indoor Basketball Court',
        'Volleyball Court',
        'Badminton Courts',
        'Changing Rooms',
        'Spectator Seating',
        'Equipment Rental',
        'Professional Coaching',
        'Tournament Hosting'
      ],
      amenities: ['Parking', 'Lockers', 'Showers', 'Pro Shop'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'sports@nlngraclub.com',
        extension: '301'
      }
    },

    'swimming-pool': {
      name: 'Swimming Pool',
      category: 'Aquatics',
      type: 'facility',
      description: 'Olympic-size swimming pool with dedicated lanes and recreational areas.',
      longDescription: 'Dive into luxury at our Olympic-size swimming pool. Whether you\'re training for competition, enjoying a leisurely swim, or teaching your little ones to swim, our pristine facilities cater to all your aquatic needs.',
      images: [
        'https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1537956965359-7573183d1f57?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1564424224827-cd24b8915874?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-swimmer',
      capacity: '100 swimmers',
      hours: 'Mon-Sun: 5am - 9pm',
      features: [
        'Olympic-size Pool',
        'Kids Pool',
        'Jacuzzi',
        'Lap Lanes',
        'Swimming Lessons',
        'Aqua Aerobics',
        'Lifeguards on Duty',
        'Poolside Lounge'
      ],
      amenities: ['Towels', 'Lockers', 'Showers', 'Poolside Cafe'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'aquatics@nlngraclub.com',
        extension: '302'
      }
    },

    'golf-course': {
      name: 'Golf Course',
      category: 'Sports',
      type: 'facility',
      description: 'Championship 18-hole golf course with stunning views and professional facilities.',
      longDescription: 'Experience world-class golf at our championship 18-hole course. Designed by renowned architects, our course offers challenges for golfers of all skill levels amidst breathtaking natural scenery.',
      images: [
        'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1593111774240-cb4fd86b0c99?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-golf-ball',
      capacity: '144 players daily',
      hours: 'Mon-Sun: 6am - 7pm',
      features: [
        '18-Hole Championship Course',
        'Driving Range',
        'Putting Green',
        'Pro Shop',
        'Golf Cart Rental',
        'Club Rental',
        'Professional Lessons',
        'Tournament Hosting'
      ],
      amenities: ['Caddy Service', 'Locker Rooms', 'Restaurant', 'Bar'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'golf@nlngraclub.com',
        extension: '303'
      }
    },

    'wellness-center': {
      name: 'Wellness Center',
      category: 'Health & Wellness',
      type: 'facility',
      description: 'Comprehensive wellness facility with gym, spa, and health services.',
      longDescription: 'Rejuvenate your body and mind at our Wellness Center. From state-of-the-art fitness equipment to relaxing spa treatments and professional health consultations, we provide everything you need for holistic wellness.',
      images: [
        'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1629909613654-28e377c37b09?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-spa',
      capacity: '100 members',
      hours: 'Mon-Sun: 5am - 10pm',
      features: [
        'Modern Gym Equipment',
        'Spa & Sauna',
        'Steam Room',
        'Massage Therapy',
        'Personal Training',
        'Nutrition Counseling',
        'Yoga Studio',
        'Health Assessments'
      ],
      amenities: ['Towels', 'Lockers', 'Juice Bar', 'Changing Rooms'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'wellness@nlngraclub.com',
        extension: '304'
      }
    },

    'tennis-courts': {
      name: 'Tennis Courts',
      category: 'Sports',
      type: 'facility',
      description: 'Professional tennis courts with lighting for day and night play.',
      longDescription: 'Perfect your serve on our professionally maintained tennis courts. With both clay and hard court options, night lighting, and professional coaching available, we cater to tennis enthusiasts at every level.',
      images: [
        'https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1608248543803-ba4f30c0b8a9?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-table-tennis',
      capacity: '4 players per court',
      hours: 'Mon-Sun: 6am - 10pm',
      features: [
        '4 Floodlit Courts',
        'Clay and Hard Courts',
        'Ball Machine Rental',
        'Racket Restringing',
        'Professional Coaching',
        'Tournament Hosting',
        'Junior Programs',
        'League Play'
      ],
      amenities: ['Pro Shop', 'Viewing Area', 'Water Station', 'Equipment Rental'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'tennis@nlngraclub.com',
        extension: '305'
      }
    },

    // ========== SECTIONS ==========
    'athletics-aerobics': {
      name: 'Athletics & Aerobics',
      category: 'Fitness',
      type: 'section',
      description: 'High-energy fitness classes and athletics training for all levels.',
      longDescription: 'Join our Athletics and Aerobics section for a comprehensive fitness experience. From high-intensity interval training to gentle aerobics, we offer classes for every fitness level. Our experienced instructors will help you achieve your health goals in a supportive group environment.',
      images: [
        'https://images.unsplash.com/photo-1534430480872-3498386e7856?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-running',
      members: '45+ active members',
      coach: 'Coach Amina Mohammed',
      schedule: 'Mon, Wed, Fri: 6pm | Sat: 9am',
      level: 'All Levels',
      features: [
        'Professional Coaching',
        'Group Fitness Classes',
        'Personal Training Available',
        'Fitness Assessments',
        'Nutrition Guidance',
        'Monthly Challenges',
        'Social Events',
        'Competition Opportunities'
      ],
      facilities: ['Aerobics Studio', 'Running Track', 'Cardio Zone', 'Strength Area'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'athletics@nlngraclub.com',
        extension: '201'
      },
      classes: ['HIIT', 'Step Aerobics', 'Circuit Training', 'Zumba', 'Bootcamp'],
      achievements: ['Regional Champions 2023', 'Best Fitness Club Award 2022']
    },

    'badminton': {
      name: 'Badminton Club',
      category: 'Racket Sports',
      type: 'section',
      description: 'Competitive and recreational badminton for all skill levels.',
      longDescription: 'Whether you\'re a beginner learning the basics or an advanced player competing in tournaments, our Badminton Club offers the perfect environment. With professional coaching, regular practice sessions, and friendly competitions, we foster a vibrant badminton community.',
      images: [
        'https://images.unsplash.com/photo-1613918431703-aa2e3c3e3e3e?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1613918431703-aa2e3c3e3e3e?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-table-tennis',
      members: '30+ active members',
      coach: 'Coach David Okonkwo',
      schedule: 'Tue, Thu: 5pm - 8pm | Sat: 10am - 1pm',
      level: 'Beginner to Advanced',
      features: [
        'Professional Coaching',
        'Regular Tournaments',
        'Equipment Available',
        'Junior Development Program',
        'Social Play Sessions',
        'Inter-club Matches',
        'Video Analysis',
        'Fitness Training'
      ],
      facilities: ['3 Indoor Courts', 'Viewing Gallery', 'Equipment Shop'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'badminton@nlngraclub.com',
        extension: '202'
      }
    },

    'basketball': {
      name: 'Basketball Section',
      category: 'Team Sports',
      type: 'section',
      description: 'Competitive basketball programs for men, women, and youth.',
      longDescription: 'Join our vibrant basketball community! From youth development programs to competitive adult leagues, we offer opportunities for players of all ages and skill levels to enjoy the game and improve their skills.',
      images: [
        'https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1504450751681-7338c13f2b93?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1519861531473-9200262188bf?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-basketball-ball',
      members: '60+ active members',
      coach: 'Coach Michael Adebayo',
      schedule: 'Team practices: Mon-Fri evenings | Games: Weekends',
      level: 'Youth, Amateur, Competitive',
      features: [
        'Youth Development Academy',
        'Men\'s League Team',
        'Women\'s League Team',
        'Skills Clinics',
        'Summer Camps',
        'Tournament Participation',
        'Strength & Conditioning',
        'Video Analysis'
      ],
      facilities: ['Full Court', 'Practice Courts', 'Locker Rooms'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'basketball@nlngraclub.com',
        extension: '203'
      }
    },

    'football': {
      name: 'Football Club',
      category: 'Team Sports',
      type: 'section',
      description: 'The beautiful game for all ages and abilities.',
      longDescription: 'Experience the passion of football with our vibrant club. From youth academies to senior teams, we provide quality coaching, competitive matches, and a supportive community for football enthusiasts.',
      images: [
        'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1529900748604-07564a03e7a6?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1517466787929-bc90951d0974?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-futbol',
      members: '120+ active members',
      coach: 'Coach Emmanuel Okoro',
      schedule: 'Training: Tue, Thu, Sat | Matches: Sundays',
      level: 'All Ages and Levels',
      features: [
        'Youth Academy (U8-U18)',
        'Men\'s Senior Team',
        'Women\'s Team',
        'Veterans Team',
        'Professional Coaching',
        'League Participation',
        'Tournament Entry',
        'Goalkeeper Training'
      ],
      facilities: ['Full-size Pitch', 'Training Ground', 'Changing Rooms'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'football@nlngraclub.com',
        extension: '204'
      }
    },

    'swimming': {
      name: 'Swimming Section',
      category: 'Aquatics',
      type: 'section',
      description: 'Competitive swimming, training, and development programs.',
      longDescription: 'Dive into excellence with our swimming section. From learn-to-swim programs for children to competitive training for advanced swimmers, we provide comprehensive aquatic education and athletic development.',
      images: [
        'https://images.unsplash.com/photo-1530549387789-4c1017266635?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1560089423-ba9d4967a22b?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1573558831201-9b2b2e99eb7d?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-swimmer',
      members: '85+ active members',
      coach: 'Coach Funke Adeyemi',
      schedule: 'Training: Mon-Fri mornings & evenings | Meets: Weekends',
      level: 'Learn-to-Swim to Competitive',
      features: [
        'Learn-to-Swim Program',
        'Age Group Swimming',
        'Senior Competitive Team',
        'Masters Swimming',
        'Technique Clinics',
        'Competition Preparation',
        'Video Analysis',
        'Dryland Training'
      ],
      facilities: ['Olympic Pool', 'Training Pool', 'Timing System'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'swimming@nlngraclub.com',
        extension: '205'
      }
    },

    'tennis-section': {
      name: 'Tennis Section',
      category: 'Racket Sports',
      type: 'section',
      description: 'Comprehensive tennis programs for juniors and adults.',
      longDescription: 'Love tennis? Join our vibrant tennis community! With programs for every age and skill level, professional coaching, and regular social events, we help members enjoy and excel at this lifetime sport.',
      images: [
        'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1528306916-9a3e6f72a4f4?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1595435934249-5df7ed86e1c0?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-table-tennis',
      members: '70+ active members',
      coach: 'Coach Tunde Williams',
      schedule: 'Daily programs available',
      level: 'Beginner to Advanced',
      features: [
        'Junior Development',
        'Adult Coaching',
        'Cardio Tennis',
        'Team Practices',
        'Inter-club Matches',
        'Tournaments',
        'Social Mixers',
        'Holiday Camps'
      ],
      facilities: ['4 Courts', 'Practice Wall', 'Ball Machines'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'tennis-section@nlngraclub.com',
        extension: '206'
      }
    },

    'yoga': {
      name: 'Yoga & Meditation',
      category: 'Wellness',
      type: 'section',
      description: 'Find your balance with yoga, meditation, and mindfulness practices.',
      longDescription: 'Discover inner peace and physical wellness through our yoga and meditation programs. From gentle Hatha to dynamic Vinyasa, and guided meditation sessions, we offer practices for all levels in a serene environment.',
      images: [
        'https://images.unsplash.com/photo-1575052814086-f385e2e2ad1b?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-om',
      members: '50+ active members',
      coach: 'Instructor Ngozi Eze',
      schedule: 'Daily classes: 7am, 9am, 5pm, 6:30pm',
      level: 'All Levels',
      features: [
        'Hatha Yoga',
        'Vinyasa Flow',
        'Restorative Yoga',
        'Prenatal Yoga',
        'Guided Meditation',
        'Pranayama (Breathing)',
        'Workshops',
        'Retreats'
      ],
      facilities: ['Serene Studio', 'Props Provided', 'Changing Area'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'yoga@nlngraclub.com',
        extension: '207'
      }
    },

    'karate': {
      name: 'Karate Dojo',
      category: 'Martial Arts',
      type: 'section',
      description: 'Traditional and competitive karate training for all ages.',
      longDescription: 'Develop discipline, respect, and self-defense skills through our karate program. Following traditional Shotokan principles, we offer a structured path from white belt to black belt and beyond.',
      images: [
        'https://images.unsplash.com/photo-1555597673-b21d5c935865?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1555597400-1a5c7f6b5f5a?q=80&w=1200&auto=format&fit=crop',
        'https://images.unsplash.com/photo-1555597400-1a5c7f6b5f5a?q=80&w=1200&auto=format&fit=crop'
      ],
      icon: 'fa-fist-raised',
      members: '40+ active members',
      coach: 'Sensei Kenji Tanaka',
      schedule: 'Tue, Thu: 5pm-7pm | Sat: 9am-12pm',
      level: 'Beginner to Black Belt',
      features: [
        'Traditional Shotokan',
        'Kata (Forms)',
        'Kumite (Sparring)',
        'Self-Defense',
        'Belt Gradings',
        'Competition Training',
        'Junior Program',
        'Weapons Training'
      ],
      facilities: ['Traditional Dojo', 'Training Mats', 'Changing Rooms'],
      contact: {
        phone: '+234 905 584 6379',
        email: 'karate@nlngraclub.com',
        extension: '208'
      }
    }
  };

  // Get the facility/section data or use default
  const facility = facilitiesData[id] || {
    name: id.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
    category: 'General',
    type: 'facility',
    description: 'Premium facility at NLNG RA Club.',
    longDescription: 'This facility offers world-class amenities and services for our members.',
    images: [
      'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?q=80&w=1200&auto=format&fit=crop',
      'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?q=80&w=1200&auto=format&fit=crop'
    ],
    icon: 'fa-building',
    capacity: 'Various',
    hours: 'Mon-Sun: 6am - 10pm',
    features: ['Premium Service', 'Member Access', 'Professional Staff'],
    amenities: ['Parking Available', 'Accessible'],
    contact: {
      phone: '+234 905 584 6379',
      email: 'info@nlngraclub.com',
      extension: '100'
    }
  };

  const isSection = facility.type === 'section';

  return (
    <div className={isSection ? 'section-detail-page' : 'facility-detail-page'}>
      {/* Hero Section */}
      <section className={isSection ? 'section-detail-hero' : 'facility-detail-hero'}>
        <div className="hero-background">
          <img src={facility.images[0]} alt={facility.name} />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to={isSection ? "/sections" : "/facilities"} className="back-link">
              <i className="fas fa-arrow-left"></i> All {isSection ? 'Sections' : 'Facilities'}
            </Link>
            <span className={isSection ? 'section-category' : 'facility-category'}>
              {facility.category}
            </span>
            <h1 className={isSection ? 'section-title' : 'facility-title'}>{facility.name}</h1>
            <p className={isSection ? 'section-subtitle' : 'facility-subtitle'}>
              {facility.description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className={isSection ? 'section-content-section' : 'facility-content-section'}>
        <div className="container">
          <div className={isSection ? 'section-grid' : 'facility-grid'}>
            {/* Left Column - Images */}
            <div className={isSection ? 'section-gallery' : 'facility-gallery'}>
              <div className="main-image">
                <img 
                  src={facility.images[activeImage]} 
                  alt={facility.name} 
                  key={activeImage}
                />
              </div>
              {facility.images.length > 1 && (
                <div className="image-thumbnails">
                  {facility.images.map((img, index) => (
                    <button
                      key={index}
                      className={`thumbnail-btn ${activeImage === index ? 'active' : ''}`}
                      onClick={() => setActiveImage(index)}
                    >
                      <img src={img} alt={`${facility.name} ${index + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className={isSection ? 'section-details' : 'facility-details'}>
              <div className="detail-section">
                <h2>About {isSection ? 'this Section' : 'this Facility'}</h2>
                <p className={isSection ? 'section-description' : 'facility-description'}>
                  {facility.longDescription}
                </p>
              </div>

              {/* Section-specific coach info */}
              {isSection && facility.coach && (
                <div className="coach-info">
                  <div className="info-card">
                    <i className="fas fa-user-tie"></i>
                    <div>
                      <h4>Head Coach</h4>
                      <p>{facility.coach}</p>
                    </div>
                  </div>
                  {facility.members && (
                    <div className="info-card">
                      <i className="fas fa-users"></i>
                      <div>
                        <h4>Members</h4>
                        <p>{facility.members}</p>
                      </div>
                    </div>
                  )}
                  {facility.schedule && (
                    <div className="info-card">
                      <i className="fas fa-clock"></i>
                      <div>
                        <h4>Schedule</h4>
                        <p>{facility.schedule}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="detail-section">
                <h3>Key Features</h3>
                <ul className="features-list">
                  {facility.features.map((feature, index) => (
                    <li key={index}>
                      <i className="fas fa-check-circle"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Info Grid - Different for sections vs facilities */}
              <div className="info-grid">
                {!isSection && (
                  <>
                    <div className="info-card">
                      <i className="fas fa-clock"></i>
                      <h4>Hours</h4>
                      <p>{facility.hours}</p>
                    </div>
                    <div className="info-card">
                      <i className="fas fa-users"></i>
                      <h4>Capacity</h4>
                      <p>{facility.capacity}</p>
                    </div>
                  </>
                )}
                <div className="info-card">
                  <i className="fas fa-phone"></i>
                  <h4>Contact</h4>
                  <p>{facility.contact.phone} ext. {facility.contact.extension}</p>
                </div>
              </div>

              {/* Additional info section */}
              {facility.classes || facility.achievements || facility.menu || facility.chef ? (
                <div className="additional-info">
                  {facility.classes && (
                    <div className="info-row">
                      <span className="info-label">Classes:</span>
                      <span className="info-value">{facility.classes.join(' • ')}</span>
                    </div>
                  )}
                  {facility.achievements && (
                    <div className="info-row">
                      <span className="info-label">Achievements:</span>
                      <span className="info-value">{facility.achievements.join(' • ')}</span>
                    </div>
                  )}
                  {facility.menu && (
                    <div className="info-row">
                      <span className="info-label">Cuisine:</span>
                      <span className="info-value">{facility.menu.join(' • ')}</span>
                    </div>
                  )}
                  {facility.chef && (
                    <div className="info-row">
                      <span className="info-label">Head Chef:</span>
                      <span className="info-value">{facility.chef}</span>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="action-buttons">
                <Link to="/login" className="btn btn-primary btn-large">
                  <i className={`fas fa-${isSection ? 'handshake' : 'calendar-check'}`}></i>
                  {isSection ? ' Join This Section' : ' Book This Facility'}
                </Link>
                <Link to="/contact" className="btn btn-outline btn-large">
                  <i className="fas fa-envelope"></i> Inquire
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FacilityDetail;